import { faPenToSquare, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Button } from "@/components/Button/Button";
import { Icon } from "@/components";
import {
	subTasksSelector,
	tasksSelector,
} from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	useGetCommentsBySubtaskQuery,
	useGetCommentsByTaskQuery,
} from "@/pages/Private/redux/tasks-and-subtasks/task-and-subtasks-activity.api";

import { timeAgo } from "@/utils/timeAgo";

import { useAppSelector } from "@/redux/hooks";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { commentsSelector } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks-activity-slice";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";

import CreateCommentInput from "./CreateCommentInput";
import EditCommentInput from "./EditCommentInput";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

interface ActivityProps {
	companyToDoProgressId: number | null;
	isTask: boolean;
}

export default function Activity({ companyToDoProgressId, isTask }: ActivityProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const tasks = useSelector(tasksSelector);
	const subtasks = useSelector(subTasksSelector);
	const data = useMemo(
		() =>
			companyToDoProgressId && isTask
				? tasks.find((item) => item.id === companyToDoProgressId)
				: subtasks.find((item) => item.id === companyToDoProgressId),
		[companyToDoProgressId, tasks, subtasks]
	);

	const dataRef = useRef(data);

	const { user: userId } = useAppSelector(authSelector);

	const { deleteComment } = useToDoSocket();

	const allComments = useSelector(commentsSelector);

	const comments = useMemo(
		() =>
			allComments.filter((c) =>
				isTask ? c.taskId === companyToDoProgressId : c.subtaskId === companyToDoProgressId
			),
		[allComments, companyToDoProgressId, isTask]
	);

	useGetCommentsByTaskQuery(data?.id ?? 0, {
		skip: !data?.id || !isTask,
	});

	useGetCommentsBySubtaskQuery(data?.id ?? 0, {
		skip: !data?.id || isTask,
	});

	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	const { data: users } = useGetAssigneesQuery();

	const userMap = useMemo(() => {
		return (users ?? []).reduce<Record<number, { firstName: string; lastName: string }>>(
			(acc, user) => {
				acc[+user.id] = {
					firstName: user?.firstName ?? "",
					lastName: user?.lastName ?? "",
				};

				return acc;
			},
			{}
		);
	}, [users]);

	const [editingCommentId, setEditingCommentId] = useState<number | null>(null);

	return (
		<>
			<h3 className="text-[16px] font-bold leading-5 text-primary-blue-dark mb-3">
				{ts("activity")}
			</h3>
			{data && <CreateCommentInput taskData={data} />}{" "}
			<div className="flex flex-col gap-3">
				{comments?.length && comments?.length > 0
					? comments?.map((comment) => {
							const user = userMap?.[comment?.userId];
							const isEditing = editingCommentId === comment?.id;
							const isTheSameUser = comment?.userId === userId?.id;

							return (
								<div
									key={comment.id}
									className="px-4 group py-3 border rounded-md border-[#E6E6EC]"
								>
									<div className="flex flex-row justify-between">
										<div className="flex flex-col justify-between">
											<h3 className="text-[16px] font-bold leading-5 text-primary-blue-dark">
												{user?.firstName} {user?.lastName}
											</h3>
											<p className="text-[12px] font-normal leading-5 text-primary-gray-lighter mb-3">
												{timeAgo(comment?.createdAt)}
											</p>
										</div>
										<div className="flex flex-row justify-between gap-2 opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 ease-out">
											{isTheSameUser ? (
												<>
													<Button
														className="!h-[30px] !w-[30px]"
														color={ButtonColor.ACTION_SECONDARY}
														image={<Icon icon={faPenToSquare} />}
														size={ButtonSize.XXS}
														title=""
														onClick={() => setEditingCommentId(comment.id ?? null)}
													/>
													<Button
														className="!h-[30px] !w-[30px]"
														color={ButtonColor.ACTION_SECONDARY}
														image={<Icon icon={faTrashCan} />}
														size={ButtonSize.XXS}
														title=""
														onClick={() => {
															if (comment?.id) {
																deleteComment(comment?.id);
															}
														}}
													/>
												</>
											) : null}
										</div>
									</div>
									{isEditing ? (
										<EditCommentInput
											commentId={comment.id!}
											initialContent={comment.content}
											onClose={() => setEditingCommentId(null)}
										/>
									) : (
										<p className="text-[16px] font-normal leading-5 text-primary-blue-dark mb-3">
											{comment?.content}
										</p>
									)}
								</div>
							);
					  })
					: null}
			</div>{" "}
		</>
	);
}
